/**
 * handlers/helpers.ts
 * Shared constants and utility functions used across handler sub-modules.
 */

import { state } from '../state';
import { dom } from '../dom';

// ── Tool-call label / cognitive-state maps ────────────────────────────────────

export const TOOL_LABELS: Record<string, string> = {
  search_judgments:   'Searching legal corpus...',
  search_legislation: 'Searching legislation...',
  search_regulations: 'Searching regulations...',
  search_cases:       'Searching case law...',
  query_database:     'Querying database...',
  get_matter:         'Retrieving matter details...',
  get_matters:        'Loading matters...',
  draft_document:     'Drafting document...',
  create_doc:         'Creating document...',
  generate_letter:    'Drafting correspondence...',
  generate_clause:    'Drafting clause...',
  recall_memory:      'Recalling from memory...',
  get_memory:         'Retrieving context...',
  get_client:         'Loading client data...',
  calculate_deadline: 'Calculating deadlines...',
  risk_analysis:      'Analysing risk factors...',
  compute_limitation: 'Computing limitation period...',
  fetch_data:         'Fetching data...',
  web_search:         'Searching the web...',
  think:              'Reasoning...',
};

export const TOOL_COG_STATE: Record<string, string> = {
  search_judgments:   'searching',
  search_legislation: 'searching',
  search_regulations: 'searching',
  search_cases:       'searching',
  query_database:     'searching',
  web_search:         'searching',
  draft_document:     'drafting',
  create_doc:         'drafting',
  generate_letter:    'drafting',
  generate_clause:    'drafting',
  recall_memory:      'recalling',
  get_memory:         'recalling',
  get_matter:         'recalling',
  get_matters:        'recalling',
  get_client:         'recalling',
  calculate_deadline: 'calculating',
  risk_analysis:      'calculating',
  compute_limitation: 'calculating',
  think:              'thinking',
};

export const FALLBACK_STORIES: string[] = [
  'Harambee: in Kenyan tradition, collective effort builds what individuals cannot. Bring this spirit to every matter you touch.',
  "The Judicature Act 1967 established Kenya's independent judiciary — remind your client that justice here has deep roots.",
  'Msaada ni nguvu: assistance is strength. Offering help freely builds trust that outlasts any single case.',
  'Kenyan courts adopted the overriding objective of expeditious justice in 2010 — speed and fairness need not conflict.',
  "Ubuntu: I am because we are. Your client's success reflects on the whole firm, and on the profession.",
];

// ── View section metadata ─────────────────────────────────────────────────────

export const SECTION_LABELS: Record<string, string> = {
  dashboard: 'Dashboard Overview',  clients:   'Client Management',
  advocates: 'Advocate Management', matters:   'Matters Portfolio',
  messages:  'Messaging',           invoices:  'Financial Management',
  documents: 'Document Management', tasks:     'Task Management',
  calendar:  'Calendar & Schedule', reports:   'Reports & Analytics',
  settings:  'Settings',            billing:   'Billing & Plan',
  activity:  'Activity Log',        warroom:   'War Room',
  time:      'Time Intelligence',   profile:   'My Profile',
};

export const SECTION_COLORS: Record<string, string> = {
  dashboard: 'rgba(0,229,255,.03)',  matters:   'rgba(201,169,110,.03)',
  invoices:  'rgba(0,207,168,.03)',  warroom:   'rgba(255,95,122,.03)',
  tasks:     'rgba(139,92,246,.03)', documents: 'rgba(59,130,246,.03)',
};

// ── Shared utilities ──────────────────────────────────────────────────────────

export function isClientUser(): boolean {
  const token = (typeof AkiliPortal !== 'undefined' && AkiliPortal.getToken)
    ? AkiliPortal.getToken() : '';
  if (token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.role === 'client') return true;
      if (payload.role === 'admin' || payload.role === 'advocate') return false;
    } catch (_) {}
  }
  if (window.location.pathname.indexOf('dashboard.html') !== -1) return true;
  return false;
}

export function getUserName(): string | undefined {
  const token = (typeof AkiliPortal !== 'undefined' && AkiliPortal.getToken)
    ? AkiliPortal.getToken() : '';
  if (token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.full_name && typeof payload.full_name === 'string') return payload.full_name;
      if (payload.name      && typeof payload.name      === 'string') return payload.name;
      if (payload.email     && typeof payload.email     === 'string') {
        const local = payload.email.split('@')[0].split(/[._-]/)[0];
        if (local.length >= 2) return local.charAt(0).toUpperCase() + local.slice(1).toLowerCase();
      }
    } catch (_) {}
  }
  try {
    if (typeof AkiliPortal !== 'undefined' && AkiliPortal.getUser) {
      const u = AkiliPortal.getUser();
      if (u && (u.full_name || u.name)) return u.full_name || u.name;
    }
  } catch (_) {}
  return undefined;
}

export function updateSendState() {
  const input   = dom.input;
  const sendBtn = dom.sendBtn;
  if (!input || !sendBtn) return;
  sendBtn.disabled = state.streaming || !input.value.trim();
}

export function focusInput() {
  setTimeout(() => {
    const inp = dom.input;
    if (inp) inp.focus();
  }, 350);
}

export function updateContextBar() {
  const section = (window as any)._currentSection || 'platform';
  const label   = SECTION_LABELS[section] || 'Platform Assistant';
  const ctxEl   = dom.ctxText;
  if (ctxEl) ctxEl.textContent = 'Context: ' + label;
  const drawer = dom.drawer;
  const color  = SECTION_COLORS[section] || 'transparent';
  if (drawer) drawer.style.backgroundImage =
    'radial-gradient(ellipse at top right,' + color + ' 0%,transparent 60%)';
}

/**
 * Hide the mode bar and disclaimer text.
 * Called by every secondary-panel switcher (History, Insights, Workflows,
 * Personal Facts) so those chrome elements don't bleed into non-chat views.
 */
export function _hideBottomChrome() {
  const modeBar    = document.getElementById('ai-mode-bar');
  const disclaimer = document.querySelector('.ai-disclaimer') as HTMLElement | null;
  if (modeBar)    modeBar.style.display    = 'none';
  if (disclaimer) disclaimer.style.display = 'none';
}

/**
 * Show or hide all navigational header buttons at once.
 * @param {'show'|'hide'} mode
 */
export function _setNavBtns(mode: 'show' | 'hide') {
  const show = mode === 'show';

  if (dom.histBtn) dom.histBtn.style.display = show ? 'flex' : 'none';

  const insWrap = dom.insightsBtn && dom.insightsBtn.parentElement;
  if (insWrap) (insWrap as HTMLElement).style.display = show ? 'flex' : 'none';

  if (dom.workflowsBtn) {
    dom.workflowsBtn.style.display = (show && !isClientUser()) ? 'flex' : 'none';
  }

  if (dom.privacyBtn) dom.privacyBtn.style.display = show ? 'flex' : 'none';
  if (dom.newsBtn)    dom.newsBtn.style.display    = show ? 'flex' : 'none';
  if (dom.backBtn)    dom.backBtn.style.display    = show ? 'none' : 'flex';
}

/**
 * Fuzzy fallback — used when toolName is not found in TOOL_COG_STATE.
 */
export function _toolNameToCognitive(toolName: string): string {
  const t = toolName.toLowerCase();
  if (t.includes('search') || t.includes('query') || t.includes('fetch')) return 'searching';
  if (t.includes('draft')  || t.includes('generate') || t.includes('create_doc')) return 'drafting';
  if (t.includes('recall') || t.includes('memory')   || t.includes('get_matter')) return 'recalling';
  if (t.includes('risk')   || t.includes('deadline') || t.includes('calc'))       return 'calculating';
  return 'thinking';
}
